/**
 * What this concept is connected to — ROUTE_INVENTORY §2.10 #45.
 *
 * This is the panel's only lateral move: selecting a row **swaps the
 * sidebar's contents without closing it**, so a user can walk a chain of
 * concepts without returning to the graph between each one. That behaviour
 * lives in the parent — here the contract is just that a row reports its id.
 *
 * ## Three kinds of "connected", and the panel used to show one of them
 *
 * The archive has three different relationships between concepts and they
 * were easy to confuse, because two were listed and the third was only ever
 * visible as a line on the canvas:
 *
 * - **Comes before** — a `prerequisite` edge. An ordering: you want this one
 *   first. Directed, and rare.
 * - **Similar** — a `similarity` edge, cosine distance between the two
 *   concepts' embeddings. Undirected, and says nothing about order.
 * - **Same lesson** — a `co_occurrence` edge, written between every pair of
 *   concepts in a lesson. Never drawn on the canvas and never listed here:
 *   there are hundreds per lesson and position already says it.
 *
 * So the rows are grouped by WHY, with the reason named. "Know first"
 * (`PrerequisitesBox`) stays separate on purpose — those come from the
 * lesson plan's own text, not from the graph, and may name things the
 * student has no concept for at all.
 *
 * ## Two things that were unreadable are gone
 *
 * Each row carried a coloured dot from `colorSlotForDomain(related.id)` —
 * which hashes whatever string it is given, and a related concept carries no
 * domain, only `{ id, title, similarity }`. So the colour was a hash of a
 * **UUID**: ten palette slots assigned at random, stable per concept and
 * meaningless, inviting a reader to decode a pattern that was never there.
 *
 * And the score read as a bare `91%`, a percentage of nothing in particular.
 */

import type { ReactNode } from 'react';
import { Share2 } from 'lucide-react';
import type { ConceptDetail } from '@/types/mindspace-concept';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import {
  PANEL_ROW,
  PANEL_ROW_INTERACTIVE,
  PanelSection,
} from '@/components/features/mindspace/concepts/PanelSection';

/** A neighbour in the graph, with the edge that put it there. */
export interface LinkedConcept {
  id: string;
  title: string;
  /** Present for similarity edges; the server's own ordering. */
  similarity?: number;
}

interface RelatedConceptListProps {
  /** Similarity neighbours: the API's `related_concepts`. */
  items: ConceptDetail['related_concepts'];
  /** `prerequisite` edges from the graph, if the caller has it. */
  comesBefore?: LinkedConcept[];
  /** How many concepts share a lesson with this one. */
  sameLessonCount?: number;
  onSelect?: (conceptId: string) => void;
}

export function RelatedConceptList({
  items,
  comesBefore = [],
  sameLessonCount = 0,
  onSelect,
}: RelatedConceptListProps) {
  if (items.length === 0 && comesBefore.length === 0 && sameLessonCount === 0) {
    return null;
  }

  return (
    <PanelSection
      icon={<Share2 className='h-3.5 w-3.5' aria-hidden />}
      title='Connected concepts'
      hint='Open any of them without leaving this panel.'
    >
      {comesBefore.length > 0 && (
        <Group label='Comes before this'>
          {comesBefore.map(item => (
            <Row
              key={item.id}
              id={item.id}
              title={item.title}
              onSelect={onSelect}
            />
          ))}
        </Group>
      )}

      {items.length > 0 && (
        <Group label='Similar in meaning'>
          {items.map(related => (
            <Row
              key={related.id}
              id={related.id}
              title={related.title}
              figure={`${Math.round(related.similarity * 100)}% similar`}
              onSelect={onSelect}
            />
          ))}
        </Group>
      )}

      {sameLessonCount > 0 && (
        <p className='mt-2 px-1.5 text-xs text-muted-foreground'>
          {sameLessonCount === 1
            ? '1 more concept came from the same lesson.'
            : `${sameLessonCount} more concepts came from the same lesson.`}{' '}
          They are not drawn as links; they sit together on the map instead.
        </p>
      )}
    </PanelSection>
  );
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='mb-2 last:mb-0'>
      <p className='px-1.5 pb-0.5 text-xs font-medium text-muted-foreground'>
        {label}
      </p>
      <ul>{children}</ul>
    </div>
  );
}

/**
 * `onSelect` is optional, and when it is absent a row renders **disabled**
 * rather than as dead text: it still carries its name and figure, which are
 * worth reading, and a disabled button says "not here" where a plain list
 * would say "not ever".
 */
function Row({
  id,
  title,
  figure,
  onSelect,
}: {
  id: string;
  title: string;
  figure?: string;
  onSelect?: (conceptId: string) => void;
}) {
  return (
    <li>
      <button
        disabled={!onSelect}
        onClick={() => onSelect?.(id)}
        className={
          onSelect ? PANEL_ROW_INTERACTIVE : `${PANEL_ROW} cursor-default`
        }
      >
        <InlineMarkdown className='min-w-0 flex-1 truncate font-medium'>
          {title}
        </InlineMarkdown>
        {figure && (
          <span className='flex-shrink-0 text-xs tabular-nums text-muted-foreground'>
            {figure}
          </span>
        )}
      </button>
    </li>
  );
}
